import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { AlertTriangle, CheckCircle, XCircle, TrendingUp } from 'lucide-react';

export const DataValidationPanel: React.FC = () => {
  const { user } = useAuth();

  const { data: validationResults, isLoading } = useQuery({
    queryKey: ['data-validation'],
    queryFn: async () => {
      if (!user) return null;

      const { data: runs, error } = await supabase
        .from('running_performance')
        .select('*')
        .order('recorded_date', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Data quality checks
      const issues: any[] = [];
      const warnings: any[] = [];
      const insights: any[] = [];

      runs?.forEach((run, index) => {
        // Check for unrealistic pace (faster than 2:30/km or slower than 12:00/km)
        if (run.pace_per_km_seconds) {
          if (run.pace_per_km_seconds < 150) {
            issues.push({
              id: `pace-too-fast-${run.id}`,
              type: 'error',
              message: `Unrealistically fast pace: ${Math.floor(run.pace_per_km_seconds / 60)}:${(run.pace_per_km_seconds % 60).toString().padStart(2, '0')}/km`,
              date: run.recorded_date,
              suggestion: 'Verify pace calculation or check for data entry errors'
            });
          } else if (run.pace_per_km_seconds > 720) {
            warnings.push({
              id: `pace-slow-${run.id}`,
              type: 'warning',
              message: `Very slow pace: ${Math.floor(run.pace_per_km_seconds / 60)}:${(run.pace_per_km_seconds % 60).toString().padStart(2, '0')}/km`,
              date: run.recorded_date,
              suggestion: 'Consider if this was a recovery run or walking interval'
            });
          }
        }

        // Check for missing heart rate data
        if (!run.heart_rate_avg && run.distance_meters && run.distance_meters > 1000) {
          warnings.push({
            id: `missing-hr-${run.id}`,
            type: 'warning',
            message: 'Missing heart rate data for significant run',
            date: run.recorded_date,
            suggestion: 'Consider adding heart rate data for better training insights'
          });
        }

        // Check for unrealistic heart rate
        if (run.heart_rate_max && run.heart_rate_max > 220) {
          issues.push({
            id: `hr-too-high-${run.id}`,
            type: 'error',
            message: `Unrealistically high max HR: ${run.heart_rate_max} bpm`,
            date: run.recorded_date,
            suggestion: 'Check heart rate monitor or data entry'
          });
        }

        // Check for pace consistency
        if (index > 0 && run.pace_per_km_seconds && runs[index - 1].pace_per_km_seconds) {
          const paceChange = Math.abs(run.pace_per_km_seconds - runs[index - 1].pace_per_km_seconds);
          if (paceChange > 120 && run.run_type === runs[index - 1].run_type) {
            insights.push({
              id: `pace-variation-${run.id}`,
              type: 'insight',
              message: `Significant pace variation in similar run types`,
              date: run.recorded_date,
              suggestion: 'Consider factors like weather, fatigue, or terrain'
            });
          }
        }
      });

      // Calculate data completeness
      const totalRuns = runs?.length || 0;
      const runsWithHR = runs?.filter(r => r.heart_rate_avg).length || 0;
      const runsWithPace = runs?.filter(r => r.pace_per_km_seconds).length || 0;
      const runsWithWeather = runs?.filter(r => r.weather_conditions || r.temperature_celsius).length || 0;

      return {
        issues,
        warnings,
        insights,
        completeness: {
          heartRate: Math.round((runsWithHR / totalRuns) * 100),
          pace: Math.round((runsWithPace / totalRuns) * 100),
          weather: Math.round((runsWithWeather / totalRuns) * 100),
          total: totalRuns
        }
      };
    },
    enabled: !!user,
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Data Quality Analysis</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">Loading validation...</div>
        </CardContent>
      </Card>
    );
  }

  if (!validationResults) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Data Quality Analysis</CardTitle>
          <CardDescription>Validate and improve your running data quality</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No data available for validation
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalIssues = validationResults.issues.length + validationResults.warnings.length;
  const dataQualityScore = Math.max(0, 100 - (totalIssues * 5));

  return (
    <div className="space-y-6">
      {/* Data Quality Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className={`text-2xl font-bold ${
              dataQualityScore >= 80 ? 'text-green-600' : 
              dataQualityScore >= 60 ? 'text-yellow-600' : 'text-red-600'
            }`}>
              {dataQualityScore}%
            </div>
            <div className="text-sm text-muted-foreground">Quality Score</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {validationResults.completeness.heartRate}%
            </div>
            <div className="text-sm text-muted-foreground">HR Data</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {validationResults.completeness.pace}%
            </div>
            <div className="text-sm text-muted-foreground">Pace Data</div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {validationResults.completeness.weather}%
            </div>
            <div className="text-sm text-muted-foreground">Weather Data</div>
          </CardContent>
        </Card>
      </div>

      {/* Issues and Warnings */}
      <Card>
        <CardHeader>
          <CardTitle>Data Quality Issues</CardTitle>
          <CardDescription>
            {totalIssues === 0 
              ? 'Great! No data quality issues found.'
              : `Found ${totalIssues} items that need attention`
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {validationResults.issues.map(issue => (
            <Alert key={issue.id} variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>
                <div className="flex justify-between items-start">
                  <div>
                    <strong>{issue.message}</strong>
                    <div className="text-sm mt-1">{issue.suggestion}</div>
                  </div>
                  <Badge variant="destructive">Error</Badge>
                </div>
              </AlertDescription>
            </Alert>
          ))}

          {validationResults.warnings.map(warning => (
            <Alert key={warning.id}>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="flex justify-between items-start">
                  <div>
                    <strong>{warning.message}</strong>
                    <div className="text-sm mt-1">{warning.suggestion}</div>
                  </div>
                  <Badge variant="secondary">Warning</Badge>
                </div>
              </AlertDescription>
            </Alert>
          ))}

          {totalIssues === 0 && (
            <Alert>
              <CheckCircle className="h-4 w-4" />
              <AlertDescription>
                Your running data looks clean and consistent. Keep up the good tracking habits!
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Insights */}
      {validationResults.insights.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Performance Insights</CardTitle>
            <CardDescription>Observations from your data patterns</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {validationResults.insights.map(insight => (
              <Alert key={insight.id}>
                <TrendingUp className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex justify-between items-start">
                    <div>
                      <strong>{insight.message}</strong>
                      <div className="text-sm mt-1">{insight.suggestion}</div>
                    </div>
                    <Badge variant="outline">Insight</Badge>
                  </div>
                </AlertDescription>
              </Alert>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};